import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Divider,
  Button,
  Grid,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Input,
  List,
  ListItem,
  TextField,
} from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import { SubmitHandler, FormHandles, Scope } from '@unform/core';
import { Form } from '@unform/web';
import { useStyles } from './styles';
import {
  VeiculoType,
  ViaType,
  VitimaType,
  DriverIncidenteDetailsType,
  Agent,
  MissionCar,
} from '../../@types/records';
import ShowSnackbar, { SetSnackbarType } from '../../components/SnackBar';
import LocalizationSection from '../../components/LocalizationFormGroup';
import VehicleFormGroup from '../../components/VehicleFormGroup';
import VictimFormGroup from '../../components/VictimFormGroup';
import IncidentDetailsForm from '../../components/IncidentDetailsForm';
import AgentVehiclesFormGroup from '../../components/AgentsVehiclesFormGroup';
import DialogConfirm from '../../components/DialogConfirm';
import ViaFormGroup from '../../components/ViaFormGroup';
import api from '../../services/api';
import { GoogleMapsReverseResponseType } from '../../@types/geocodingTypes';
import DialogWarnActions, { DialogActionType } from '../../components/DialogWarnActions';
import { generateId } from '../../utils';
import { validadeObjectProps } from './utils';
import Colors from '../../components/Colors';

interface FormData {
  data: {
    driverIncidenteDetails: DriverIncidenteDetailsType;
    driverVia: ViaType[];
    driverVitima: VitimaType[];
    driverVeiculo: VeiculoType[];
    driverAgentsAndVehicles: { agents: Agent[]; missionCars: MissionCar[] };
  };
  geom: string;
  occurred_from: string;
  occurred_to: string;
  schema: string;
  location_text: string;
  city: string;
  county: string;
  road: string;
  state: string;
}

interface Coordinates {
  lat: number;
  lng: number;
}

const initialAddress: GoogleMapsReverseResponseType = {
  status: '',
  plus_code: {
    compound_code: '',
    global_code: '',
  },
  results: [
    {
      address_components: [
        {
          long_name: '',
          short_name: '',
          types: [' '],
        },
      ],
      formatted_address: '',
      geometry: {
        location: {
          lat: 0,
          lng: 0,
        },
        location_type: '',
      },
    },
  ],
};

const initialAgents: Agent[] = [
  { Matrícula: '', _localId: generateId() },
  { Matrícula: '', _localId: generateId() },
  { Matrícula: '', _localId: generateId() },
  { Matrícula: '', _localId: generateId() },
];

const initialMissionCars: MissionCar[] = [
  { id: '', _localId: generateId() },
  { id: '', _localId: generateId() },
  { id: '', _localId: generateId() },
];

export interface ViaObject extends ViaType {
  index: number;
}

export interface VehicleObject extends VeiculoType {
  index: number;
}

export interface VictimObject extends VitimaType {
  index: number;
}

export interface ResponseRecordType {
  uuid: string;
  archived?: boolean;
  city: string;
  city_district: string;
  county: string;
  created_by: string;
  created: string;
  data: {
    driverIncidenteDetails: DriverIncidenteDetailsType;
    driverVeiculo: VeiculoType[];
    driverVia: ViaType[];
    driverVitima: VitimaType[];
    driverAgentsAndVehicles: {
      agents: Agent[];
      missionCars: MissionCar[];
      _localId: string;
    };
  };
  geom: {
    coordinates: string[];
    type: string;
  };
  light: string | null;
  location_text: string;
  modified: string;
  modified_by: string;
  neighborhood: string;
  occurred_from: string;
  occurred_to: string;
  road: string;
  schema: string;
  state: string;
  weather: string;
}

interface routeParams {
  pathId: string;
}

interface UpdateLocation {
  address1: string;
  address2?: string;
  refPoint?: string;
  neighborhood?: string;
}
interface IncidentImages {
  path: string;
  uuid: string;
  description: string;
}

const genViaNames = (names: ViaObject[]) => {
  const viaNames = names.map((item, index) => item.Nome || `Via ${index + 1}`);
  viaNames.unshift('Não informado');
  return viaNames;
};

const UpdateIncidentSAMU: React.FC = () => {
  const defaultIncidentDetails: DriverIncidenteDetailsType = {
    'Controle de Tráfego': '',
    'Código do Acidente': '',
    'Elementos Atingidas': '',
    'Endereço 1': '',
    'Endereço 2': '',
    'Hora informada': '',
    'Ocorrido ao Longo da Via/Cruzamento': '',
    'Primeira Fonte de Informação': '',
    'Superfície da Pista': '',
    'Tipo de Cruzamento': '',
    Iluminação: '',
    Interseção: '',
    Jurisdição: '',
    Local: '',
    Natureza: '',
    Severidade: '',
    Tempo: '',
    'Código AMC': '',
    'Código PEFOCE': '',
    'Código PRE': '',
    'Código PRF': '',
    'Código SAMU': '',
    'Código SIM': '',
    _localId: '',
  };
  const [updateLocation, setUpdateLocation] = useState<UpdateLocation>({
    address1: '',
    address2: '',
    neighborhood: '',
    refPoint: '',
  });
  const [driverIncidentDetails, setDriverIncidentDetails] = useState<
    DriverIncidenteDetailsType
  >(defaultIncidentDetails);
  const [snackBar, setSnackbar] = useState<SetSnackbarType>({ open: false, message: '' });
  const [location, setLocation] = useState<GoogleMapsReverseResponseType>(initialAddress);
  const [driverVehicles, setDriverVehicles] = useState<VehicleObject[]>([]);
  const [driverVictims, setDriverVictims] = useState<VictimObject[]>([]);
  const [latlng, setLatLng] = useState<Coordinates>({ lat: 0, lng: 0 });
  const [requiredFields, setRequiredFields] = useState<
    { key: string; message: string }[]
  >([]);
  const [dialogConfirm, setDialogConfirm] = useState<boolean>(false);
  const [driverVias, setDriverVias] = useState<ViaObject[]>([]);
  const [incidentSchema, setIncidentSchema] = useState('');
  const [loading, setLoading] = useState<boolean>(false);
  const [period, setPeriod] = useState('');
  const [imagens, setImagens] = useState<IncidentImages[]>([]);
  const [image, setImage] = useState<File[]>([]);
  const [imageLength, setImageLength] = useState<number>(0);
  const [removedImages, setRemovedImages] = useState<string[]>([]);
  const [reference, setReference] = useState<string>('');
  const [adresses, setAdresses] = useState<{
    address1: string;
    address2: string;
    neighborhood: string;
  }>({
    address1: '',
    address2: '',
    neighborhood: '',
  });
  const [agentAndVehicles, setAgentsAndVehicles] = useState<{
    agents: Agent[];
    missionCars: MissionCar[];
    _localId: string;
  }>({ agents: initialAgents, missionCars: initialMissionCars, _localId: generateId() });
  const [dialogAction, setDialogAction] = useState<DialogActionType>({
    open: false,
    title: '',
    actions: [],
  });
  const [imgDescriptions, setImgDescriptions] = useState<string[]>([]);

  const classes = useStyles();

  const formRef = useRef<FormHandles>(null);

  const history = useHistory();
  const navigate = useCallback(
    (path: string) => {
      history.push(path);
    },
    [history],
  );
  const { pathId } = useParams<routeParams>();

  const authHeader = useMemo(() => {
    return { Authorization: `Token ${localStorage.getItem('auth')}` };
  }, []);

  const fetchImages = useCallback(async () => {
    const { data: file } = await api.get<IncidentImages[]>(
      '/api/samu_records/get_images/',
      {
        headers: authHeader,
        params: {
          recordID: pathId,
        },
      },
    );
    file.forEach((item) => (item.description ? '' : item.description));
    setImagens(file);
    setImgDescriptions(file.map((item) => item.description));
  }, [authHeader, pathId]);

  const inputProps = useMemo(() => {
    return {
      multiple: false,
      accept: 'image/*',
      disabled: true,
    };
  }, []);
  const inputProps2 = useMemo(() => {
    return {
      multiple: false,
      accept: 'image/*',
    };
  }, []);
  const [inputEnable, setInputEnable] = useState(inputProps2);
  const arrayImagens = image;

  const arquivar = useCallback(
    (event: any) => {
      if (event.target.files && event.target.files[0]) {
        arrayImagens.push(event.target.files[0]);
        setImagens([
          ...imagens,
          { uuid: '', path: URL.createObjectURL(event.target.files[0]), description: '' },
        ]);
        setImage(arrayImagens);
        setImageLength(imageLength + 1);
        imageLength === 14 ? setInputEnable(inputProps) : setInputEnable(inputProps2);
      }
    },
    [arrayImagens, imageLength, inputProps, inputProps2, imagens],
  );

  function deleteImage(index: number) {
    const removed = imagens.splice(index, 1);
    setRemovedImages([...removedImages, removed[0].uuid]);
    setImage(arrayImagens);
    setImgDescriptions((prev) => {
      prev.splice(index, 1);
      return [...prev];
    });
    setImageLength(imageLength - 1);
  }

  const postImages = useCallback(
    async (recordID: string) => {
      const imagem = new FormData();
      image.forEach((file, index) => {
        imagem.append(`files[${index}]`, file);
      });

      imagem.append('recordID', recordID);
      imagem.append('descriptions', imgDescriptions.toString());
      await api.put('/api/samu_records/update_image/', imagem, {
        headers: { Authorization: `Token ${localStorage.getItem('auth')}` },
        params: {
          removedImg: removedImages.toString() !== '' ? removedImages.toString() : null,
        },
      });
    },
    [image, imgDescriptions, removedImages],
  );

  const handleImgDescCange = (value: string, index: number) => {
    setImgDescriptions((prev) => {
      prev[index] = value;
      return [...prev];
    });
  };

  useEffect(() => {
    setLoading(true);
    try {
      const fetchIncident = async () => {
        const { data: incident } = await api.get<ResponseRecordType>(
          `/api/samu_records/${pathId}/`,
          {
            headers: authHeader,
          },
        );

        const incidentRecord = validadeObjectProps(incident);

        const incidentVia: ViaObject[] = incidentRecord.data.driverVia.map((i, ix) => {
          return { index: ix, ...i };
        });
        const incidentVictim: VictimObject[] = incidentRecord.data.driverVitima.map(
          (i, ix) => {
            return { index: ix, ...i };
          },
        );
        const incidentVehicle: VehicleObject[] = incidentRecord.data.driverVeiculo.map(
          (i, ix) => {
            return { index: ix, ...i };
          },
        );
        const incidentCoordinate: Coordinates = {
          lat: Number(incidentRecord.geom.coordinates[1]),
          lng: Number(incidentRecord.geom.coordinates[0]),
        };

        setDriverVias(incidentVia);
        setDriverVehicles(incidentVehicle);
        setDriverVictims(incidentVictim);
        setLatLng(incidentCoordinate);
        setPeriod(incidentRecord.occurred_to);
        setUpdateLocation({
          address1: incidentRecord.data.driverIncidenteDetails['Endereço 1'],
          address2: incidentRecord.data.driverIncidenteDetails['Endereço 2'],
          neighborhood: incidentRecord.neighborhood,
          refPoint: '',
        });
        setDriverIncidentDetails(incidentRecord.data.driverIncidenteDetails);
        setIncidentSchema(incidentRecord.schema);
        if (incidentRecord.data.driverAgentsAndVehicles) {
          setAgentsAndVehicles(incidentRecord.data.driverAgentsAndVehicles);
        }
        setAdresses({
          address1: incidentRecord.data.driverIncidenteDetails['Endereço 1'],
          address2: incidentRecord.data.driverIncidenteDetails['Endereço 2'],
          neighborhood: incidentRecord.data.driverIncidenteDetails.Bairro as string,
        });
        setReference(
          incidentRecord.data.driverIncidenteDetails['Numero Referencia']
            ? incidentRecord.data.driverIncidenteDetails['Numero Referencia']
            : '',
        );

        setLoading(false);
      };
      fetchIncident();
      fetchImages();
    } catch (err) {
      setLoading(false);
      setDialogAction({
        open: true,
        title: 'Não foi possível encontrar o incidente.',
        actions: [
          {
            btnText: 'Registros',
            color: 'primary',
            action: () => navigate('/registros/sinistros'),
          },
          {
            btnText: 'Mapa',
            color: 'primary',
            action: () => navigate('/mapa/sinistros'),
          },
          {
            btnText: 'Novo Incidente',
            color: 'primary',
            action: () => navigate('/add/sinistro'),
          },
        ],
      });
    }
  }, [pathId, navigate, authHeader, fetchImages]);

  const updateRecord = async (record: FormData) => {
    setLoading(true);
    try {
      const { data } = await api.put(`api/samu_records/${pathId}/`, record, {
        headers: authHeader,
      });
      postImages(pathId);
      setLoading(false);
      data
        ? setDialogAction({
            open: true,
            title: 'O incidente foi atualizado com sucesso!.',
            actions: [
              {
                btnText: 'Registros',
                color: 'primary',
                action: () => navigate('/registros/sinistros'),
              },
              {
                btnText: 'Mapa',
                color: 'primary',
                action: () => navigate('/mapa/sinistros'),
              },
              {
                btnText: 'Exibir',
                color: 'primary',
                action: () => navigate(`/registro/sinistro/${data.uuid}`),
              },
            ],
          })
        : setSnackbar({ open: true, message: 'O incidente não pode ser atualizado.' });
      return;
    } catch (error) {
      setLoading(false);
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar incidente!',
      });
    }
  };

  const validateFields = (fieldsData: FormData) => {
    setRequiredFields([]);
    const fieldNames: { key: string; message: string }[] = [];

    if (!fieldsData.occurred_from) {
      fieldNames.push({
        key: 'incidentDate',
        message: 'A data é hora do incidente são obrigatórias.',
      });
    }

    if (fieldsData.data.driverIncidenteDetails['Endereço 1'] === 'Não informado') {
      fieldNames.push({ key: 'Endereço 1', message: 'O endereço 1 é obrigatório.' });
    }

    if (driverVias.length > 0) {
      driverVias.map((item, index) => {
        if (!item.Nome) {
          fieldNames.push({
            key: `NomeVia ${index}`,
            message: `O nome da Via ${index} é o brigatório.`,
          });
        }
      });
    }
    if (driverVictims.length > 0) {
      driverVictims.map((item, index) => {
        if (!item.Nome) item.Nome = 'Desconhecido';
        if (!item.Idade)
          fieldNames.push({
            key: `VictimAge ${index}`,
            message: `A idade do Envolvido ${index} é obrigatório.`,
          });
        if (driverVehicles.length === 0) item['Código Veículo'] = 'Nenhum';
      });
    }

    if (fieldNames.length > 0) {
      setSnackbar({
        open: true,
        message: 'Preencha os campos obrigatórios antes de salvar.',
      });
      setRequiredFields(fieldNames);
      return false;
    }
    updateRecord(fieldsData);
    window.scrollTo(0, 0);
    return true;
  };

  const handleSubmit: SubmitHandler<FormData> = async (fieldsData) => {
    const bodyForm: FormData = { ...fieldsData };

    const viaList = [...driverVias];

    const vehiclesList = [...driverVehicles];

    const victimsList = [...driverVictims];

    bodyForm.schema = incidentSchema;
    bodyForm.geom = `POINT(${latlng.lng} ${latlng.lat})`;
    bodyForm.data.driverIncidenteDetails['Endereço 1'] = adresses.address1;
    bodyForm.data.driverIncidenteDetails['Endereço 2'] = adresses.address2;
    bodyForm.data.driverIncidenteDetails['Numero Referencia'] = reference;
    bodyForm.occurred_from = period;
    bodyForm.occurred_to = period;
    bodyForm.data.driverVia = viaList;
    bodyForm.data.driverVeiculo = vehiclesList;
    bodyForm.data.driverVitima = victimsList;
    bodyForm.data.driverIncidenteDetails = { ...driverIncidentDetails };
    bodyForm.data.driverIncidenteDetails.Bairro = adresses.neighborhood;
    bodyForm.data.driverIncidenteDetails._localId = driverIncidentDetails?._localId;
    bodyForm.data.driverAgentsAndVehicles = agentAndVehicles;
    validateFields(bodyForm);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/api/samu_records/${pathId}/`, {
        headers: authHeader,
      });
      setLoading(false);
      setDialogAction({
        open: true,
        title: 'O incidente foi removido com sucesso!.',
        actions: [
          {
            btnText: 'Registros',
            color: 'primary',
            action: () => navigate('/registros/sinistros'),
          },
          {
            btnText: 'Mapa',
            color: 'primary',
            action: () => navigate('/mapa/sinistros'),
          },
          {
            btnText: 'Novo incidente',
            color: 'primary',
            action: () => navigate('/add/sinistro'),
          },
        ],
      });
    } catch (err) {
      setLoading(false);
      setSnackbar({ message: 'Não foi possível remover o incidente!', open: true });
    }
  };

  return (
    <Container className={classes.root}>
      <Typography variant="h5">Incidente Formulário de Entrada</Typography>
      <Divider />
      <Form
        className={classes.formGroup}
        autoComplete="off"
        ref={formRef}
        onSubmit={handleSubmit}
      >
        <Grid container xs={12}>
          <Grid container spacing={2} sm={12} md={10}>
            <LocalizationSection
              setLocation={setLocation}
              latlng={latlng}
              setLatLng={setLatLng}
              period={period}
              setPeriod={setPeriod}
              updateLocation={updateLocation}
              requiredFields={requiredFields}
              adresses={adresses}
              setAdresses={setAdresses}
              reference={reference}
              setReference={setReference}
            />
            <Grid item sm={12} xs={12}>
              {!driverIncidentDetails ? (
                <Accordion className={classes.accordionContainer}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">Detalhes do Incidente</Typography>
                  </AccordionSummary>
                </Accordion>
              ) : (
                <Accordion className={classes.accordionContainer}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">Detalhes do Incidente</Typography>
                  </AccordionSummary>
                  <AccordionDetails style={{ display: 'flex', flexDirection: 'column' }}>
                    <Scope path="data.driverIncidenteDetails">
                      <IncidentDetailsForm
                        incidentDetails={driverIncidentDetails}
                        setIncidentDetails={setDriverIncidentDetails}
                        isUpdate
                      />
                    </Scope>
                  </AccordionDetails>
                </Accordion>
              )}
            </Grid>
            <Grid item sm={12} xs={12}>
              <ViaFormGroup
                driverVias={driverVias}
                setDriverVias={setDriverVias}
                requiredFields={requiredFields}
                adresses={adresses}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <VehicleFormGroup
                driverVehicles={driverVehicles}
                setDriverVehicles={setDriverVehicles}
                viaNames={genViaNames(driverVias)}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <VictimFormGroup
                driverVictims={driverVictims}
                setDriverVictims={setDriverVictims}
                vehicles={driverVehicles}
                requiredFields={requiredFields}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <Accordion
                className={classes.accordionContainer}
                onClick={() => setImageLength(imagens.length)}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">Anexos</Typography>{' '}
                </AccordionSummary>
                <AccordionDetails style={{ display: 'flex', flexDirection: 'column' }}>
                  <Container className={classes.addButtonContainer}>
                    <div className={classes.maxItens}>
                      {imagens.length} de 15 imagens adicionadas
                    </div>

                    <Input
                      id="fileItem"
                      type="file"
                      inputProps={inputEnable}
                      onChange={(event) => arquivar(event)}
                    />

                    <Container className={classes.previewContainer}>
                      <List component="ul" className={classes.previewList}>
                        {imagens.map((item, index) => (
                          <>
                            <ListItem className={classes.previewListItem}>
                              <div className={classes.previewItem}>
                                <img
                                  src={item.path}
                                  alt="imagem do incidente"
                                  className={classes.preview}
                                />
                                <div
                                  style={{
                                    display: 'fex',
                                    flexDirection: 'column',
                                    width: '100%',
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    style={{ marginBottom: 5 }}
                                  >
                                    Descrição
                                  </Typography>
                                  <TextField
                                    size="small"
                                    variant="outlined"
                                    placeholder="Descrição"
                                    value={imgDescriptions[index]}
                                    onChange={(event) =>
                                      handleImgDescCange(event.target.value, index)
                                    }
                                    className={classes.imgDescription}
                                    multiline
                                    rows={3}
                                    rowsMax={6}
                                  />
                                </div>
                              </div>
                              <Button
                                variant="outlined"
                                onClick={() => {
                                  deleteImage(index);
                                }}
                              >
                                Remover
                              </Button>
                            </ListItem>
                          </>
                        ))}
                      </List>
                    </Container>
                  </Container>
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item sm={12} xs={12}>
              <AgentVehiclesFormGroup
                agentsAndVehicles={agentAndVehicles}
                setAgentsAndVehicles={setAgentsAndVehicles}
              />
            </Grid>
          </Grid>
          <Grid>
            <div className={classes.buttonsContainer}>
              <div className={classes.buttonWrapper}>
                <Button
                  className={classes.buttons}
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={loading}
                  style={{ backgroundColor: Colors.main }}
                >
                  Atualizar Incidente
                </Button>
                {loading && (
                  <CircularProgress size={24} className={classes.buttonProgress} />
                )}
              </div>
              <div className={classes.buttonWrapper}>
                <Button
                  className={classes.buttons}
                  variant="contained"
                  onClick={() => setDialogConfirm(true)}
                  disabled={loading}
                  style={{ backgroundColor: '#C59054', color: 'white' }}
                >
                  Excluir Incidente
                </Button>
                {loading && (
                  <CircularProgress size={24} className={classes.buttonProgress} />
                )}
              </div>
              <div className={classes.buttonWrapper}>
                <Button
                  className={classes.buttons}
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate('/registros/sinistros')}
                  disabled={loading}
                  style={{ backgroundColor: 'white' }}
                >
                  Cancelar
                </Button>
                {loading && (
                  <CircularProgress
                    size={24}
                    color="secondary"
                    className={classes.buttonProgress}
                  />
                )}
              </div>
            </div>
          </Grid>
        </Grid>
      </Form>
      <ShowSnackbar
        open={snackBar.open}
        message={snackBar.message}
        setSnackbar={setSnackbar}
      />
      <DialogConfirm
        open={dialogConfirm}
        title="Apagar Incidente?"
        message="Voce está prestes a apagar esse incidente. Essa operação não pode ser desfeita. Você deseja confirmar essa ação?"
        setOpen={setDialogConfirm}
        onConfirm={() => handleDelete()}
      />
      <DialogWarnActions dialogActions={dialogAction} setDialog={setDialogAction} />
    </Container>
  );
};

export default UpdateIncidentSAMU;
